/**
 * Framework-agnostic money DISPLAY formatting.
 *
 * The platform stores amounts as a plain number plus a currency (an ISO 4217 code,
 * optionally a symbol and a decimal-places override from the host `Currency` row).
 * This module turns that into a localized string via `Intl.NumberFormat`, with a
 * deterministic fallback so output is machine-stable regardless of the host OS
 * locale - the same principle as the date org-format seam.
 *
 * It is pure - no React, no host access - so it lives in core-utils behind the
 * `@ethisyscore/core-utils/money` sub-path. Resolving WHICH currency to render in
 * (the amount's own currency, or the org's reporting currency) and CONVERTING
 * between currencies are host-aware concerns handled by the plugin-ui `useCurrency`
 * hook, which formats through this module.
 *
 * When no currency is given, `formatMoney` falls back to the organisation's
 * reporting currency via the {@link ./orgCurrency} seam (the same module-ref +
 * fail-open-to-`GBP` mechanism as the date org-format seam), so a plugin can render
 * reporting-currency amounts without threading a code or hardcoding one.
 */

import {
  getOrgReportingCurrency,
  MONEY_FALLBACK_CURRENCY,
} from "./orgCurrency";

/**
 * Deterministic fallback locale. Matches the date seam's UK fallback so money and
 * dates render consistently when no explicit locale is supplied, and so output does
 * not drift with the OS locale of whatever host renders it.
 */
export const MONEY_FALLBACK_LOCALE = "en-GB";

/** Options for {@link formatMoney}. */
export interface FormatMoneyOptions {
  /**
   * ISO 4217 code (e.g. `"GBP"`, `"EUR"`) - drives `Intl` currency formatting.
   * Optional: when omitted, `formatMoney` resolves the organisation's reporting
   * currency (see {@link ./orgCurrency}), falling back to `GBP` when none is stamped.
   */
  currencyCode?: string;
  /**
   * Fraction digits to show. When null/undefined, `Intl`'s per-currency default is
   * used (GBP -> 2, JPY -> 0). Pass a number to force a currency's decimal-places
   * override from the host `Currency` row.
   */
  decimalPlaces?: number | null;
  /** BCP-47 locale; defaults to {@link MONEY_FALLBACK_LOCALE} for stable output. */
  locale?: string;
  /**
   * Symbol to use only in the fallback path when `Intl` cannot format the currency
   * code (e.g. a non-ISO custom code). Ignored on the happy path, where `Intl`
   * supplies the symbol.
   */
  symbol?: string | null;
}

/**
 * Formats a monetary amount. The currency is resolved in this order:
 *
 * - `formatMoney(amount, "EUR")` — a positional ISO code, with the currency's
 *   natural decimals.
 * - `formatMoney(amount, { currencyCode, decimalPlaces, locale, symbol })` — the
 *   options form; any of `currencyCode`/`decimalPlaces` may be omitted.
 * - `formatMoney(amount)` (or an options object without `currencyCode`) — the
 *   organisation's reporting currency stamped via the {@link ./orgCurrency} seam,
 *   including that currency's decimal-places override when the caller gave none.
 *   When no org currency is stamped it falls back to {@link MONEY_FALLBACK_CURRENCY}
 *   (`GBP`), the same deterministic fail-open as the date org-format seam.
 *
 * On an unknown/invalid currency code (which makes `Intl.NumberFormat` throw) it
 * falls back to a symbol/code prefix plus the fixed-decimal amount, so a bad code
 * degrades to a readable string rather than throwing on a render path.
 */
export function formatMoney(amount: number, currency?: string | FormatMoneyOptions): string {
  const options: FormatMoneyOptions = typeof currency === "string" ? { currencyCode: currency } : (currency ?? {});
  const { currencyCode, decimalPlaces, locale = MONEY_FALLBACK_LOCALE, symbol } = options;

  // No explicit code (undefined arg, or an options object without currencyCode)
  // resolves the org's reporting currency, falling back to GBP when none is stamped.
  const orgCurrency = currencyCode === undefined ? getOrgReportingCurrency() : null;
  const effectiveCode = currencyCode ?? orgCurrency?.code ?? MONEY_FALLBACK_CURRENCY;
  // The org currency's decimal-places override applies ONLY when the caller omitted
  // decimalPlaces entirely (undefined). An explicit `null` means "use Intl's
  // per-currency default" and must NOT pick up the org override, so distinguish
  // undefined from null rather than coalescing both with `??`.
  const effectiveDecimalPlaces =
    decimalPlaces !== undefined ? decimalPlaces : (orgCurrency?.decimalPlaces ?? null);

  // Intl.NumberFormat requires a well-formed (uppercase) ISO 4217 code; a lowercase
  // code (e.g. "usd") throws and forces the fallback, so normalise up front.
  const normalizedCode = effectiveCode.toUpperCase();

  try {
    const formatter = new Intl.NumberFormat(locale, {
      style: "currency",
      currency: normalizedCode,
      // Narrow symbol so a clear currency context renders "$100", not "US$100".
      currencyDisplay: "narrowSymbol",
      ...(effectiveDecimalPlaces != null
        ? { minimumFractionDigits: effectiveDecimalPlaces, maximumFractionDigits: effectiveDecimalPlaces }
        : {}),
    });
    return formatter.format(amount);
  } catch {
    const fractionDigits = effectiveDecimalPlaces ?? 2;
    const prefix = symbol ?? normalizedCode;
    // Keep the minus sign before the symbol/code prefix ("-£10.00", not "£-10.00").
    const sign = amount < 0 ? "-" : "";
    return `${sign}${prefix}${Math.abs(amount).toFixed(fractionDigits)}`;
  }
}

/** Options for {@link formatCompactMoney}. */
export interface FormatCompactMoneyOptions {
  /** ISO 4217 code (e.g. `"GBP"`, `"EUR"`) - resolves the leading symbol. */
  currencyCode: string;
  /**
   * Fallback symbol used ONLY when `Intl` cannot resolve `currencyCode` (a non-ISO
   * custom code). For a valid ISO code `Intl`'s own symbol always wins - this option
   * does not override it. Mirrors {@link FormatMoneyOptions.symbol} and the
   * {@link getCurrencySymbol} fallback contract.
   */
  symbol?: string | null;
  /** BCP-47 locale used only to resolve the symbol; defaults to {@link MONEY_FALLBACK_LOCALE}. */
  locale?: string;
}

/**
 * Formats an amount as an abbreviated currency string for compact display, such
 * as chart axis ticks where space is tight - e.g. `"£1.3m"`, `"£500k"`, `"£99"`.
 *
 * Thresholds on the absolute value: >= 1,000,000 renders in millions with a
 * lowercase `m` and one decimal place; >= 1,000 renders in thousands with a
 * lowercase `k` and no decimals; otherwise the whole amount with no decimals.
 * A leading minus is kept before the symbol (e.g. `"-£1.3m"`).
 *
 * Unlike {@link formatMoney} this is NOT org-locale driven beyond resolving the
 * leading symbol via {@link getCurrencySymbol}; the number itself is formatted
 * with fixed abbreviations so axis labels stay short and machine-stable.
 */
export function formatCompactMoney(amount: number, options: FormatCompactMoneyOptions): string {
  const { currencyCode, symbol, locale = MONEY_FALLBACK_LOCALE } = options;

  const sym = getCurrencySymbol(currencyCode, locale, symbol);
  const sign = amount < 0 ? "-" : "";
  const abs = Math.abs(amount);

  if (abs >= 1_000_000) {
    return `${sign}${sym}${(abs / 1_000_000).toFixed(1)}m`;
  }
  if (abs >= 1_000) {
    return `${sign}${sym}${(abs / 1_000).toFixed(0)}k`;
  }
  return `${sign}${sym}${abs.toFixed(0)}`;
}

/**
 * Resolves the currency symbol for a code in a locale (e.g. `"GBP"` -> `"£"`),
 * for use in input adornments and labels. Falls back to the supplied `symbol`, then
 * the code itself, when `Intl` cannot resolve it.
 */
export function getCurrencySymbol(
  currencyCode: string,
  locale: string = MONEY_FALLBACK_LOCALE,
  symbol?: string | null,
): string {
  // Intl needs an uppercase ISO code; a lowercase one throws (see formatMoney).
  const normalizedCode = currencyCode.toUpperCase();

  try {
    const parts = new Intl.NumberFormat(locale, {
      style: "currency",
      currency: normalizedCode,
      currencyDisplay: "narrowSymbol",
    }).formatToParts(0);

    return parts.find((part) => part.type === "currency")?.value ?? symbol ?? normalizedCode;
  } catch {
    return symbol ?? normalizedCode;
  }
}
